import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { OptionItem } from './OptionItem';

type Props = {
  options: { label: string; count: number }[];
  selectedOptionsSet: Set<string>;
  onOptionSelect: (option: string) => void;
  onClose: () => void;
};

export const OptionsList = memo(
  ({ options, selectedOptionsSet, onOptionSelect, onClose }: Props) => {
    const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const [focusedIndex, setFocusedIndex] = useState(0);

    const focusOption = useCallback((index: number) => {
      setFocusedIndex(index);
      optionRefs.current[index]?.focus({ preventScroll: true });
    }, []);

    useEffect(() => {
      focusOption(0);
    }, [focusOption]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
      e.preventDefault();
      if (e.key === 'ArrowDown') focusOption((focusedIndex + 1) % options.length);
      else if (e.key === 'ArrowUp')
        focusOption((focusedIndex - 1 + options.length) % options.length);
      else if (e.key === 'Home') focusOption(0);
      else if (e.key === 'End') focusOption(options.length - 1);
      else if (e.key === ' ' || e.key === 'Enter') onOptionSelect(options[focusedIndex].label);
      else if (e.key === 'Escape' || e.key === 'Tab') onClose();
    };

    return (
      <div role="group" onKeyDown={handleKeyDown}>
        {options.map((option, i) => (
          <OptionItem
            key={option.label}
            ref={el => {
              optionRefs.current[i] = el;
            }}
            option={option}
            isSelected={selectedOptionsSet.has(option.label)}
            onSelect={() => onOptionSelect(option.label)}
            tabIndex={i === focusedIndex ? 0 : -1}
          />
        ))}
      </div>
    );
  },
);
